import { can as canRole, type Permission } from '@mm/shared';
import { startAuthentication } from '@simplewebauthn/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  api,
  type CurrentUser,
  isLockedResult,
  type LockedUser,
  refreshSession,
  type SessionPayload,
  setAccessToken,
  setSessionExpiredHandler,
  setSessionLockedHandler,
} from '../../api/client';

type Status = 'loading' | 'authenticated' | 'locked' | 'anonymous';

export interface SecurityInfo {
  hasPin: boolean;
  pinSetAt: string | null;
  idleLockMinutes: number;
  passkeys: { id: string; deviceName: string; createdAt: string; lastUsedAt: string | null }[];
}

interface AuthContextValue {
  status: Status;
  user: CurrentUser | null;
  /** Who the lock screen is for (also set after a page reload on a locked session). */
  lockedUser: LockedUser | null;
  security: SecurityInfo | undefined;
  login: (email: string, password: string) => Promise<void>;
  loginWithPasskey: () => Promise<void>;
  logout: () => Promise<void>;
  applySession: (session: SessionPayload) => void;
  can: (permission: Permission) => boolean;
  /** The user turned the app lock on (a PIN or a fingerprint / Face ID). */
  lockEnabled: boolean;
  lockNow: () => Promise<void>;
  unlock: (input: { pin: string } | { password: string }) => Promise<void>;
  unlockWithPasskey: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export const SECURITY_KEY = ['auth', 'security'];

/** Tabs of the same browser lock, unlock and share activity together. */
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('mm-auth') : null;
type Message = { type: 'lock' | 'unlock' | 'logout' | 'activity' };
const broadcast = (type: Message['type']) => channel?.postMessage({ type } satisfies Message);

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel', 'scroll', 'mousemove'];
const REPORT_EVERY_MS = 60_000;
const SHARE_EVERY_MS = 10_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [lockedUser, setLockedUser] = useState<LockedUser | null>(null);

  const security = useQuery({
    queryKey: SECURITY_KEY,
    queryFn: () => api.get<SecurityInfo>('/auth/security'),
    enabled: status === 'authenticated',
    staleTime: 5 * 60_000,
  });

  const applySession = useCallback((session: SessionPayload) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    setLockedUser(null);
    setStatus('authenticated');
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setLockedUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const showLock = useCallback((who: LockedUser) => {
    setAccessToken(null);
    setLockedUser(who);
    setStatus('locked');
  }, []);

  // Restore the session from the httpOnly refresh cookie on page load.
  useEffect(() => {
    let cancelled = false;
    refreshSession().then((session) => {
      if (cancelled) return;
      if (isLockedResult(session)) showLock(session.user);
      else if (session) applySession(session);
      else setStatus('anonymous');
    });
    setSessionExpiredHandler(clearSession);
    setSessionLockedHandler(showLock);
    return () => {
      cancelled = true;
      setSessionExpiredHandler(null);
      setSessionLockedHandler(null);
    };
  }, [applySession, clearSession, showLock]);

  const lockNow = useCallback(async () => {
    const info = queryClient.getQueryData<SecurityInfo>(SECURITY_KEY);
    if (user) {
      showLock({
        name: user.name,
        email: user.email,
        hasPin: info?.hasPin ?? true,
        hasPasskey: (info?.passkeys.length ?? 0) > 0,
      });
    }
    broadcast('lock');
    await fetch('/api/auth/lock', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
  }, [queryClient, showLock, user]);

  // Other tabs: follow their lock / unlock / sign-out.
  useEffect(() => {
    if (!channel) return;
    const onMessage = (e: MessageEvent<Message>) => {
      if (e.data.type === 'lock' && status === 'authenticated') {
        void refreshSession().then((r) => isLockedResult(r) && showLock(r.user));
      } else if (e.data.type === 'unlock' && status === 'locked') {
        void refreshSession().then((r) => r && !isLockedResult(r) && applySession(r));
      } else if (e.data.type === 'logout') {
        clearSession();
      }
    };
    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
  }, [status, showLock, applySession, clearSession]);

  // Idle lock: report activity now and then; lock after the configured idle time.
  const lastActivity = useRef(Date.now());
  const lockEnabled =
    !!security.data && (security.data.hasPin || security.data.passkeys.length > 0);
  const idleMinutes = lockEnabled ? security.data?.idleLockMinutes : undefined;
  useEffect(() => {
    if (status !== 'authenticated' || !idleMinutes) return;
    lastActivity.current = Date.now();
    let lastReport = Date.now();
    let lastShare = 0;
    const onActivity = () => {
      const now = Date.now();
      lastActivity.current = now;
      if (now - lastShare > SHARE_EVERY_MS) {
        lastShare = now;
        broadcast('activity');
      }
      if (now - lastReport > REPORT_EVERY_MS) {
        lastReport = now;
        void fetch('/api/auth/activity', { method: 'POST', credentials: 'same-origin' })
          .then((r) => r.json())
          .then((b: { data?: { locked?: boolean } }) => b.data?.locked && void lockNow())
          .catch(() => {});
      }
    };
    const onShared = (e: MessageEvent<Message>) => {
      if (e.data.type === 'activity') lastActivity.current = Date.now();
    };
    const check = () => {
      if (Date.now() - lastActivity.current > idleMinutes * 60_000) void lockNow();
    };
    const onVisible = () => document.visibilityState === 'visible' && check();
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });
    channel?.addEventListener('message', onShared);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, 10_000);
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
      channel?.removeEventListener('message', onShared);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [status, idleMinutes, lockNow]);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<SessionPayload>('/auth/login', { email, password });
      applySession(session);
    },
    [applySession],
  );

  const loginWithPasskey = useCallback(async () => {
    const { challengeId, options } = await api.post<{
      challengeId: string;
      options: Parameters<typeof startAuthentication>[0]['optionsJSON'];
    }>('/auth/passkey/login/options');
    const response = await startAuthentication({ optionsJSON: options });
    applySession(await api.post<SessionPayload>('/auth/passkey/login', { challengeId, response }));
  }, [applySession]);

  const unlock = useCallback(
    async (input: { pin: string } | { password: string }) => {
      applySession(await api.post<SessionPayload>('/auth/unlock', input));
      broadcast('unlock');
    },
    [applySession],
  );

  const unlockWithPasskey = useCallback(async () => {
    const options = await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
      '/auth/unlock/passkey/options',
    );
    const response = await startAuthentication({ optionsJSON: options });
    applySession(await api.post<SessionPayload>('/auth/unlock/passkey', { response }));
    broadcast('unlock');
  }, [applySession]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      broadcast('logout');
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      lockedUser,
      security: security.data,
      lockEnabled,
      login,
      loginWithPasskey,
      logout,
      applySession,
      can: (permission) => canRole(user?.role, permission),
      lockNow,
      unlock,
      unlockWithPasskey,
    }),
    [
      status,
      user,
      lockedUser,
      security.data,
      lockEnabled,
      login,
      loginWithPasskey,
      logout,
      applySession,
      lockNow,
      unlock,
      unlockWithPasskey,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
